import { useEffect } from "react";
import GradientBanner from "../../componentes/Banner";
import PageHeader from "../../componentes/PageHeader";
import MetaTags from "../../componentes/MetaTags";
import { useTranslation } from "react-i18next";
import {
  FaShieldAlt,
  FaEye,
  FaHistory,
  FaClock,
  FaUsersCog,
  FaStar,
  FaCheckCircle,
} from "react-icons/fa";
import {
  faInfoCircle,
  faLandmark,
  faUsers,
  faBuilding,
  faShieldAlt,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faBug,
  faExclamationTriangle,
  faBell,
  faChartBar,
  faTools,
  faCogs,
  faUsersCog as faUsersCogFA,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";


const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
};

const AboutEthioCert = () => {
  const { t } = useTranslation();
  // local state for news is deferred; kept minimal to avoid unused vars
  useEffect(() => {
    // placeholder effect to preserve original behavior if news fetching is added later
  }, [])

  const navigate = useNavigate();

  const handleReadMore = () => navigate("/rfc-2350");
  const handleFaq = () => navigate("/faq");
  const handleContactUs = () => navigate("/contact-us");

  // placeholder effect

  const serviceList = [
    {
      serviceId: "Pro_Active_Vulnerability_Analysis",
      icon: faBug,
      title: t("home.our_services.service1.title"),
      description: t("home.our_services.service1.description"),
    },
    {
      serviceId: "Cyber_Incident_Monitoring",
      icon: faBell,
      title: t("home.our_services.service2.title"),
      description: t("home.our_services.service2.description"),
    },
    {
      serviceId: "Cyber_Incident_Response",
      icon: faExclamationTriangle,
      title: t("home.our_services.service3.title"),
      description: t("home.our_services.service3.description"),
    },
    {
      serviceId: "Cyber_Threat_Analysis",
      icon: faLock,
      title: t("home.our_services.service4.title"),
      description: t("home.our_services.service4.description"),
    },
    {
      serviceId: "Threat_Hunting",
      icon: faChartBar,
      title: t("home.our_services.service5.title"),
      description: t("home.our_services.service5.description"),
    },
    {
      serviceId: "SOC_as_a_Service",
      icon: faTools,
      title: t("home.our_services.service6.title"),
      description: t("home.our_services.service6.description"),
    },
    {
      serviceId: "Cyber_Defense_Training",
      icon: faCogs,
      title: t("home.our_services.service7.title"),
      description: t("home.our_services.service7.description"),
    },
    {
      serviceId: "Consultancy_Service",
      icon: faUsersCogFA,
      title: t("home.our_services.service8.title"),
      description: t("home.our_services.service8.description"),
    },
  ];

  const valuesList = [
    {
      description: t("about_ethio_cert.excellence"),
    },
    {
      description: t("about_ethio_cert.collabration"),
    },
    {
      description: t("about_ethio_cert.innovation"),
    },
    {
      description: t("about_ethio_cert.integrity"),
    },
    {
      description: t("about_ethio_cert.people_centric"),
    },
  ];

  const activitiesList = [
    {
      description: t("about_ethio_cert.proactive_readiness"),
    },
    {
      description: t("about_ethio_cert.incident_response"),
    },
    {
      description: t("about_ethio_cert.capacity_building"),
    },
    {
      description: t("about_ethio_cert.colabration_and_coordination"),
    },
    {
      description: t("about_ethio_cert.policy_development"),
    },
  ];

  return (
    <>
  <MetaTags title="About Ethio-CERT" description="Learn about Ethio-CERT's mission, vision, core values and national cyber defense activities." url="https://ethiocert.gov.et/about-ethio-cert" />
      <PageHeader />
      <GradientBanner
        title={t("header.about_ethio_cert")}
        description={t("about_ethio_cert.subtitle")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.about_ethio_cert"), link: null },
        ]}
        backgroundIcons={[
          faInfoCircle,
          faLandmark,
          faUsers,
          faBuilding,
          faShieldAlt,
          faGlobe,
        ]}
      />
      <div className="min-h-screen">
        <div className="flex flex-col md:flex-row max-xl:px-4 py-8 md:py-16 px-dynamic gap-2 lg:gap-6">
          {/* Main Content */}
          <div className="flex-1 mb-4">
            <div className="flex-1 pb-4 pl-4 pr-4 mb-2 border-l-4 border-primary rounded-lg bg-gray-50">
              <div className="flex items-center space-x-3">
                <h2 className="text-lg font-medium text-gray-800 font-nunito-sans">
                  {t("about_ethio_cert.missionandvision")}
                </h2>
              </div>
              <p className="mt-4 text-gray-700 leading-relaxed font-nunito-sans">
                {t("about_ethio_cert.mivdis")}
              </p>
            </div>

            <motion.div
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              className="mt-12 grid md:grid-cols-2 gap-6"
            >
              <div className="bg-primary rounded-lg p-6 text-white transform hover:scale-105 transition-transform duration-300 shadow-xl">
                <div className="flex items-center mb-4">
                  <FaShieldAlt className="text-3xl mr-3" />
                  <h2 className="text-lg font-medium font-nunito-sans">
                    {t("about_ethio_cert.mission")}
                  </h2>
                </div>
                <p className="leading-relaxed opacity-90 font-nunito-sans">
                  {t("about_ethio_cert.mission1")}
                </p>
              </div>

              <div className="bg-primary rounded-lg p-6 text-white transform hover:scale-105 transition-transform duration-300 shadow-xl">
                <div className="flex items-center mb-4">
                  <FaEye className="text-3xl mr-3" />
                  <h2 className="text-lg font-medium font-nunito-sans">
                    {t("about_ethio_cert.vision")}
                  </h2>
                </div>
                <p className="leading-relaxed opacity-90 font-nunito-sans">
                  {t("about_ethio_cert.vision1")}
                </p>
              </div>
            </motion.div>

            <motion.div
              initial="initial"
              animate="animate"
              variants={fadeInUp}
              className="bg-white rounded-lg shadow-lg p-4 mt-6 md:mt-8 border-l-4 border-primary"
            >
              <div className="flex items-center mb-4">
                <FaUsersCog className="text-4xl mr-3 bg-primary text-white rounded-lg p-2" />
                <h2 className="text-lg font-medium text-gray-800 font-nunito-sans">
                  {t("home.who we are.title")}
                </h2>
              </div>
              <p className="text-gray-700 leading-relaxed text-justify font-nunito-sans">
                {t("about_ethio_cert.disc1")}
              </p>
              {/* <div className="my-6 border-t border-gray-100"></div>
              <p className="text-gray-700 leading-relaxed text-justify font-nunito-sans">
                {t("about_ethio_cert.disc2")}
              </p>
              <div className="my-6 border-t border-gray-100"></div>
              <p className="text-gray-700 leading-relaxed text-justify font-nunito-sans">
                {t("about_ethio_cert.disc3")}
              </p> */}
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="initial"
              animate="animate"
              className="mt-12 space-y-8"
            >
              <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-primary hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <FaStar className="text-3xl text-primary mr-3" />
                  <h2 className="text-lg font-medium text-gray-800 font-nunito-sans">
                    {t("about_ethio_cert.values")}
                  </h2>
                </div>
                <div className="space-y-4 pl-4">
                  {valuesList.map((value, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-4 bg-indigo-50 rounded-lg"
                    >
                      <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary"></div>
                      <p className="text-gray-700 font-nunito-sans">
                        {(() => {
                          const [boldText, ...rest] =
                            value.description.split(":");
                          return (
                            <>
                              <span className="font-bold">{boldText}</span>
                              {rest.length > 0 && `: ${rest.join(":")}`}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-primary hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <FaHistory className="text-3xl text-primary mr-3" />
                  <h2 className="text-lg font-medium text-gray-800 font-nunito-sans">
                    {t("about_ethio_cert.background_history")}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed text-justify mb-4 font-nunito-sans">
                  {t("about_ethio_cert.backgh1")}
                </p>
                <div className="my-4 border-t border-gray-100"></div>
                <p className="text-gray-700 leading-relaxed text-justify font-nunito-sans">
                  {t("about_ethio_cert.backgh2")}
                </p>
              </div>

              

              <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-primary hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <FaCheckCircle className="text-3xl text-primary mr-3" />
                  <h2 className="text-lg font-medium text-gray-800 font-nunito-sans">
                    {t("about_ethio_cert.main_activities")}
                  </h2>
                </div>
                <div className="space-y-4 pl-4">
                  {activitiesList.map((activity, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 p-4 bg-indigo-50 rounded-lg"
                    >
                      <div className="flex-shrink-0 w-2 h-2 mt-2 rounded-full bg-primary"></div>
                      <p className="text-gray-700 font-nunito-sans">
                        {(() => {
                          const [boldText, ...rest] =
                            activity.description.split(":");
                          return (
                            <>
                              <span className="font-bold">{boldText}</span>
                              {rest.length > 0 && `: ${rest.join(":")}`}
                            </>
                          );
                        })()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>


              <div className="bg-white rounded-lg p-6 shadow-lg border-l-4 border-primary hover:shadow-xl transition-shadow duration-300">
                <div className="flex items-center mb-4">
                  <FaClock className="text-3xl text-primary mr-3" />
                  <h2 className="text-lg font-medium text-gray-800 font-nunito-sans">
                    {t("about_ethio_cert.operations_and_accessibility")}
                  </h2>
                </div>
                <p className="text-gray-700 leading-relaxed text-justify mb-4 font-nunito-sans">
                  {t("about_ethio_cert.opracc1")}
                </p>
                <p className="text-gray-700 leading-relaxed text-justify font-nunito-sans">
                  {t("about_ethio_cert.opracc2")}
                </p>
              </div>
            </motion.div>

            <div className="max-lg:gap-6 gap-32 flex items-start justify-start mt-8">
              <button
                onClick={handleContactUs}
                className="w-full sm:w-auto text-nowrap bg-primary/80 text-white px-2 sm:px-5 py-1 sm:py-2 text-base sm:text-lg rounded-md shadow-lg hover:shadow-xl transition duration-300 flex items-center justify-center gap-2 font-nunito-sans font-medium"
              >
                {t("header.contact_us")} →
              </button>
              <button
                onClick={handleFaq}
                className="w-full sm:w-auto text-nowrap bg-primary/80 text-white px-2 sm:px-5 py-1 sm:py-2 text-base sm:text-lg rounded-md shadow-lg hover:shadow-xl transition duration-300 flex items-center justify-center gap-2 font-nunito-sans font-medium"
              >
                {t("header.faq")} →
              </button>
              <button
                onClick={handleReadMore}
                className="w-full sm:w-auto text-nowrap bg-primary/80 text-white px-2 sm:px-5 py-1 sm:py-2 text-base sm:text-lg rounded-md shadow-lg hover:shadow-xl transition duration-300 flex items-center justify-center gap-2 font-nunito-sans font-medium"
              >
                {t("header.rfc_2350")} →
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <div className="hidden md:block md:w-[30%] w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="bg-gradient-to-br from-white to-blue-50 rounded-2xl shadow-2xl p-6 md:p-8 relative overflow-hidden sticky top-24 max-xl:top-32"
            >
              <div className="absolute top-0 right-0 opacity-10">
                <svg
                  width="200"
                  height="200"
                  viewBox="0 0 200 200"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill="#4F46E5"
                    d="M40,0 L60,0 L100,40 L40,100 L0,60 Z"
                  />
                  <circle cx="150" cy="50" r="30" fill="#4F46E5" />
                  <rect x="120" y="100" width="60" height="60" fill="#4F46E5" />
                </svg>
              </div>

              <div className="flex items-start mb-8 relative z-10 border-l-4 border-primary rounded-md">
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-800 font-nunito-sans tracking-wide">
                    {t("header.our_services")}
                  </h3>
                  <p className="text-gray-700 mt-1 leading-relaxed font-nunito-sans">
                    {t(
                      "about_ethio_cert.coredisc",
                      "Explore our main services designed to protect and secure cyber-environment"
                    )}
                  </p>
                </div>
              </div>

              <div className="grid gap-6 relative z-10">
                {serviceList.map((service) => (
                  <motion.div
                    key={service.serviceId}
                    whileHover={{ y: -5 }}
                    onClick={() =>
                      navigate(
                        `/services/${encodeURIComponent(service.serviceId)}`
                      )
                    }
                    className="group relative rounded-lg p-2 bg-white shadow-md hover:shadow-lg border border-gray-200 hover:border-gray-300 transition-all duration-300 cursor-pointer overflow-hidden max-lg:p-1"
                  >
                    <div className="absolute left-0 top-0 w-2 h-full transition-all duration-300 group-hover:w-3 max-md:w-1 max-md:group-hover:w-2" />
                    <div className="flex items-start space-x-4 max-md:space-x-3">
                      <div
                        className="flex-shrink-0 max-xl:w-8 max-xl:h-8 w-10 h-10 rounded-full flex items-center justify-center text-primary text-lg md:text-xl transition-all duration-300 group-hover:scale-105"
                        style={{ backgroundColor: "#BFDBFE" }}
                      >
                        <FontAwesomeIcon icon={service.icon} />
                      </div>
                      <div className="flex-1">
                        <h4 className="lg:text-lg font-medium text-gray-800 font-nunito-sans mb-1 group-hover:text-primary transition-colors duration-300">
                          {service.title}
                        </h4>
                        <p className="text-gray-700 leading-relaxed font-nunito-sans line-clamp-2 opacity-90 group-hover:text-gray-800">
                          {service.description}
                        </p>
                        <a className="text-primary text-sm font-medium mt-1 md:mt-2 inline-block opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-nunito-sans">
                          {t("header.readmore")}
                        </a>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutEthioCert;
